// src/pages/admin/products/StockAlerts.jsx
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Package,
  AlertTriangle,
  Search,
  RefreshCw,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Filter,
  ChevronDown
} from 'lucide-react'

// UI Components
import { Button } from '../../../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import OrangeLoadingSpinner from '../../../components/ui/OrangeLoadingSpinner'
import CardLoader from '../../../components/ui/CardLoader'

// Services and Utils
import StockAlertsService from '../../../services/stockAlertsService'
import BranchService from '../../../services/branchService'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import { 
  filterAlerts, 
  sortAlerts, 
  calculateAlertsSummary,
  formatAlertItem,
  getStatusConfig
} from '../../../utils/stockAlertsUtils'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 
    type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

const StockAlerts = () => {
  // Navigation
  const navigate = useNavigate()

  // Get current user and permissions
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)

  // State Management
  const [alerts, setAlerts] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')

  // Filters and Search
  const [filters, setFilters] = useState({
    branchId: 'all',
    status: 'all',
    itemType: 'all',
    searchTerm: ''
  })
  const [sortConfig, setSortConfig] = useState({
    field: 'priority',
    order: 'desc'
  })

  // UI State
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  // Initialize filters for branch-restricted users
  useEffect(() => {
    if (permissions.isBranchRestricted && currentUser?.branchId) {
      console.log('🎯 [STOCK ALERTS] Auto-setting branch filter for manager:', {
        branchId: currentUser.branchId,
        userRole: currentUser?.role
      })
      setFilters(prev => ({
        ...prev,
        branchId: currentUser.branchId
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.isBranchRestricted, currentUser?.branchId])

  // Clear all filters
  const clearFilters = () => {
    const defaultFilters = {
      branchId: permissions.isBranchRestricted && currentUser?.branchId ? 
                currentUser.branchId : 'all',
      status: 'all',
      itemType: 'all',
      searchTerm: ''
    }
    setFilters(defaultFilters)
    setSortConfig({
      field: 'priority',
      order: 'desc'
    })
  }

  // Data fetching - using pattern from RawMaterialsInventory to prevent infinite reloads
  useEffect(() => {
    // Fetch stock alerts
    const fetchStockAlerts = async (showLoader = true) => {
      try {
        if (showLoader) setLoading(true)
        else setRefreshing(true)

        // Debug logging
        console.log('🔍 StockAlerts Debug Info:')
        console.log('- Current User:', currentUser)
        console.log('- User Role:', currentUser?.role)
        console.log('- User Branch ID:', currentUser?.branchId)
        console.log('- Permissions isBranchRestricted:', permissions.isBranchRestricted)
        console.log('- Permissions canAccessAllBranches:', permissions.canAccessAllBranches)

        // Log branch access for security
        permissions.logBranchAccess('stock_alerts', currentUser?.branchId)

        let response
        if (permissions.isBranchRestricted && currentUser?.branchId) {
          // Branch-restricted users only see their branch alerts
          console.log('🔒 Fetching branch-specific alerts for branch:', currentUser.branchId)
          response = await StockAlertsService.getStockAlertsByBranch(currentUser.branchId)
        } else {
          // Admins see all alerts, but can filter by branch
          console.log('👑 Admin user - fetching all alerts with filter:', filters.branchId)
          const params = filters.branchId !== 'all' ? { branch: filters.branchId } : {}
          response = await StockAlertsService.getAllStockAlerts(params)
        }

        if (response.success && response.data?.items) {
          console.log('✅ Stock alerts received:', {
            count: response.data.items.length,
            items: response.data.items,
            isBranchRestricted: permissions.isBranchRestricted,
            userBranchId: currentUser?.branchId
          })
          setAlerts(response.data.items)
          setError('')
        } else {
          throw new Error(response.message || 'Failed to fetch stock alerts')
        }
      } catch (err) {
        console.error('Error fetching stock alerts:', err)
        setError(err.message || 'Failed to fetch stock alerts')
        setToast({ show: true, message: err.message || 'Failed to fetch stock alerts', type: 'error' })
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000)
      } finally {
        if (showLoader) setLoading(false)
        else setRefreshing(false)
      }
    }

    // Fetch branches (admin only)
    const fetchBranches = async () => {
      if (!permissions.canAccessAllBranches) return

      try {
        const response = await BranchService.getAllBranches()
        if (response.success && response.data) {
          // Handle different possible response structures
          const branchData = Array.isArray(response.data) ? response.data : 
                            response.data.branches ? response.data.branches :
                            response.data.items ? response.data.items : []
          setBranches(branchData)
        }
      } catch (err) {
        console.error('Error fetching branches:', err)
        setBranches([]) // Ensure branches is always an array
      }
    }

    // Initial data load
    fetchStockAlerts()
    fetchBranches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array for initial load only - prevents infinite reloads

  // Separate effect for filter changes (admin users only)
  useEffect(() => {
    if (!permissions.canAccessAllBranches) return // Skip for branch-restricted users
    
    const fetchFilteredData = async () => {
      try {
        setRefreshing(true)
        
        const params = filters.branchId !== 'all' ? { branch: filters.branchId } : {}
        const response = await StockAlertsService.getAllStockAlerts(params)

        if (response.success && response.data?.items) {
          setAlerts(response.data.items)
          setError('')
        } else {
          throw new Error(response.message || 'Failed to fetch stock alerts')
        }
      } catch (err) {
        console.error('Error fetching filtered stock alerts:', err)
        setError(err.message || 'Failed to fetch stock alerts')
      } finally {
        setRefreshing(false)
      }
    }

    fetchFilteredData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branchId]) // Only depend on branchId filter changes

  // Create separate function for manual refresh
  const fetchData = async () => {
    try {
      setRefreshing(true)

      // Debug logging
      console.log('🔄 Manual refresh - StockAlerts Debug Info:')
      console.log('- Current User:', currentUser)
      console.log('- User Role:', currentUser?.role)
      console.log('- User Branch ID:', currentUser?.branchId)
      console.log('- Permissions isBranchRestricted:', permissions.isBranchRestricted)

      // Log branch access for security
      permissions.logBranchAccess('stock_alerts', currentUser?.branchId)

      let response
      if (permissions.isBranchRestricted && currentUser?.branchId) {
        // Branch-restricted users only see their branch alerts
        console.log('🔒 Manual refresh - Fetching branch-specific alerts for branch:', currentUser.branchId)
        response = await StockAlertsService.getStockAlertsByBranch(currentUser.branchId)
      } else {
        // Admins see all alerts, but can filter by branch
        console.log('👑 Manual refresh - Admin user, fetching all alerts with filter:', filters.branchId)
        const params = filters.branchId !== 'all' ? { branch: filters.branchId } : {}
        response = await StockAlertsService.getAllStockAlerts(params)
      }

      if (response.success && response.data?.items) {
        console.log('✅ Manual refresh - Stock alerts received:', {
          count: response.data.items.length,
          items: response.data.items,
          isBranchRestricted: permissions.isBranchRestricted,
          userBranchId: currentUser?.branchId
        })
        setAlerts(response.data.items)
        setError('')
      } else {
        throw new Error(response.message || 'Failed to fetch stock alerts')
      }
    } catch (err) {
      console.error('Error fetching stock alerts:', err)
      setError(err.message || 'Failed to fetch stock alerts')
      setToast({ show: true, message: err.message || 'Failed to fetch stock alerts', type: 'error' })
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000)
    } finally {
      setRefreshing(false)
    }
  }

  // Apply filters and sorting
  const processedAlerts = useCallback(() => {
    console.log('🔍 [PROCESSING] Processing alerts:', {
      totalAlerts: alerts.length,
      filters,
      isBranchRestricted: permissions.isBranchRestricted,
      userBranchId: currentUser?.branchId
    })

    let processed = filterAlerts(alerts, filters)
    
    console.log('✅ [FILTERED] After filtering:', {
      originalCount: alerts.length,
      filteredCount: processed.length,
      appliedFilters: filters
    })
    
    processed = sortAlerts(processed, sortConfig.field, sortConfig.order)
    return processed.map(formatAlertItem)
  }, [alerts, filters, sortConfig, permissions.isBranchRestricted, currentUser?.branchId])

  const filteredAlerts = processedAlerts()
  const summary = calculateAlertsSummary(filteredAlerts)

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }))
  }

  // Handle refresh
  const handleRefresh = () => {
    fetchData()
  }

  // Navigate to Purchase Orders
  const handleGoToPurchaseOrder = () => {
    navigate('/admin/purchase-order/all')
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Stock Alerts
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            onClick={handleGoToPurchaseOrder}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Purchase Order
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                <p className="text-2xl font-bold text-gray-900">{summary.byStatus.outOfStock}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-gray-900">{summary.byStatus.lowStock}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building2 className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Affected Branches</p>
                <p className="text-2xl font-bold text-gray-900">{summary.affectedBranchesCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Alerts</h2>
            </div>
            {(filters.searchTerm || filters.branchId !== 'all' || filters.status !== 'all' || filters.itemType !== 'all') && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search items..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Branch Filter - Only show for users who can access all branches */}
            <div className="relative">
              <select
                value={filters.branchId}
                onChange={(e) => handleFilterChange('branchId', e.target.value)}
                disabled={permissions.isBranchRestricted}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white ${
                  permissions.isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="all">All Branches</option>
                {Array.isArray(branches) && branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Status</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="low_stock">Low Stock</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing {filteredAlerts.length} of {alerts.length} stock alerts
          </div>
        </CardContent>
      </Card>

      {/* Stock Alerts Table */}
      {loading ? (
        <CardLoader title="Loading stock alerts..." />
      ) : error ? (
        <Card className="bg-white">
          <CardContent className="py-12">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => fetchData()} leftIcon={RefreshCw}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {alerts.length === 0 ? 'No stock alerts' : 'No alerts match your filters'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {alerts.length === 0 
                    ? 'All inventory levels are normal.' 
                    : 'Try adjusting your search or filter criteria.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ITEM
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CURRENT STOCK
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        REQUIRED LEVEL
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STATUS
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        BRANCH
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAlerts.map((alert) => {
                      const statusConfig = getStatusConfig(alert.stockStatus)
                      // Debug: Log alert structure to understand branch data
                      if (filteredAlerts.indexOf(alert) === 0) {
                        console.log('🔍 [TABLE DEBUG] First alert structure:', {
                          alert,
                          branchName: alert.branch?.name,
                          branchId: alert.branch?._id,
                          branchObject: alert.branch
                        })
                      }
                      return (
                        <tr key={alert._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                                  alert.isProduct ? 'bg-blue-100' : 'bg-green-100'
                                }`}>
                                  <Package className={`h-4 w-4 ${
                                    alert.isProduct ? 'text-blue-600' : 'text-green-600'
                                  }`} />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {alert.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {alert.itemCode}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {alert.formattedStock}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {alert.formattedThreshold}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.badgeColor}`}>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                              {alert.branch?.name || alert.branchName || 'Unknown Branch'}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default StockAlerts